import { useEffect, useMemo, useState } from 'react'
import Btn from '../../../../UI/Btn'
import Table from '../../../../UI/Table'
import FilePreview from '../../../../UI/FilePreview'
import MiniHeader from '../../../../UI/MiniHeader'
import { useTranslation } from 'react-i18next'
import { add, differenceInDays, sub } from 'date-fns'

import styles from './style.module.scss'
import ReportsCard from '../../../../UI/ReportsCard'
import Search from '../../../../UI/Search'
import BtnFilter from '../../../../UI/BtnFilter'
import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'
import BtnTooltip from '../../../../UI/BtnTooltip'
import { blobTypes, customNumberFormat, downloadFile, stateFullLabel, stateLabel } from '../../../../shared/helpers'
import { useIsReadOnly } from '../../../../shared/alumni'
import { reportTypeLabel } from '../../../../shared/reportLabel'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	branchField,
	companyTypeField,
	createdWithinField,
	invoiceStatusField,
	schoolField,
	stateField
} from '../../../../UI/FilterPanel/fields'
import Pagination from '../../../../UI/Pagination'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import clsx from 'clsx'
import Icon from '../../../../UI/Icon'
import { connect } from 'react-redux'
import { formatVienna } from '../../../../shared/datetime'

const roundAmount = value => Math.round((value || 0) * 100) / 100

// Die Kacheln filtern die Liste — dieselbe Aufteilung, aus der auch die Beträge
// der Kacheln stammen. Gutschriften stehen bei den bezahlten: es ist nichts mehr
// offen, ihr Betrag ist negativ.
const STATUS_GROUPS = {
	total: [],
	paid: ['Paid', 'Credit'],
	open: ['Unpaid', 'Overdue'],
	// Storniert steht neben Gesamt, nicht darin: die Rechnung ist weder bezahlt
	// noch offen (Anforderung #91).
	cancelled: ['Cancelled']
}

const sameStatusSet = (a, b) => a.length === b.length && a.every(value => b.includes(value))

function Invoices(props) {
	const { t, i18n } = useTranslation()
	const [search, setSearch] = useState('')
	const [invoices, setInvoices] = useState([])
	const [paginationCount, setPaginationCount] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	// Position der Rechnung in der Vorschau; -1 heisst: keine Vorschau offen.
	const [previewIndex, setPreviewIndex] = useState(-1)
	const [stats, setStats] = useState({
		totalAmount: 0,
		totalCount: 0,
		paidAmount: 0,
		paidCount: 0,
		openAmount: 0,
		openCount: 0,
		overdueCount: 0,
		creditAmount: 0,
		creditCount: 0,
		cancelledAmount: 0,
		cancelledCount: 0,
		openCreditAmount: 0,
		openCreditCount: 0
	})
	const [openFilter, setOpenFilter] = useState(false)
	const [loading, setLoading] = useState(false)
	const readOnly = useIsReadOnly()
	const limit = 10

	// Bundesland-Admins sehen ohnehin nur ihr eigenes Bundesland (das setzt das
	// Backend), ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			invoiceStatusField(t),
			companyTypeField(t),
			branchField(t, props.branch, i18n.language),
			schoolField(t),
			// Ziel der Kachel „Neu" auf der Startseite (shared/widgetListLink).
			createdWithinField(t)
		],
		[t, i18n.language, props.role, props.branch]
	)

	const filters = useFilters({ storageKey: 'invoices', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchInvoices = () => {
		const isCurrent = latest()

		http.get(TApi.ADMIN_GET_INVOICES, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			const arrayTable = data.rows.map(v => {
				const createdAt = new Date(v.createdAt)
				const overdue = createdAt < sub(new Date(), { days: data.daysOverdue })
				return {
					...v,
					name: v.Report.Company.name,
					type: v.Report.Company.companyType,
					state: v.Report.Company.state,
					period: v.Report.type,
					createdAt: formatVienna(createdAt, 'dd.MM.yyyy'),
					school: v.Report.Company?.TeachersSupervisions?.[0]?.Teacher?.School?.name,
					overdueDays: differenceInDays(add(createdAt, { days: data.daysOverdue }), new Date()),
					statusStyle:
						(v.status === 'Cancelled' && styles.cancelled) ||
						(v.status === 'Unpaid' && overdue && styles.red) ||
						(v.status === 'Paid' && styles.green),
					overdue
				}
			})
			// Neue Liste, alte Position: Die Vorschau zeigt sonst die Rechnung,
			// die an dieser Stelle vorher stand.
			setPreviewIndex(-1)
			setInvoices(arrayTable)
			setPaginationCount(data.count)
			setStats({
				totalAmount: roundAmount(data.totalAmount),
				totalCount: data.totalCount ?? data.count ?? 0,
				paidAmount: roundAmount(data.paidAmount),
				paidCount: data.paidCount ?? data.paidInvoices ?? 0,
				openAmount: roundAmount(data.openAmount),
				openCount: data.openCount ?? 0,
				overdueCount: data.overdueCount ?? data.overdueInvoices ?? 0,
				creditAmount: roundAmount(data.creditAmount),
				creditCount: data.creditCount ?? 0,
				cancelledAmount: roundAmount(data.cancelledAmount),
				cancelledCount: data.cancelledCount ?? 0,
				openCreditAmount: roundAmount(data.openCreditAmount),
				openCreditCount: data.openCreditCount ?? 0
			})
		})
	}
	// Anteil am Gesamtbetrag — die Kacheln zeigen Betrag, Anzahl und Prozent derselben Menge.
	const amountShare = amount => {
		const share = stats.totalAmount ? (amount / stats.totalAmount) * 100 : 0
		return `${customNumberFormat(share.toFixed(2))} %`
	}

	const selectedStatus = (filters.values.invoiceStatus || []).map(item => item.value)
	const activeGroup =
		(!selectedStatus.length && 'total') ||
		(sameStatusSet(selectedStatus, STATUS_GROUPS.paid) && 'paid') ||
		(sameStatusSet(selectedStatus, STATUS_GROUPS.open) && 'open') ||
		(sameStatusSet(selectedStatus, STATUS_GROUPS.cancelled) && 'cancelled') ||
		null

	// Klick auf eine Kachel filtert die Liste, der Klick auf die aktive Kachel
	// hebt den Filter wieder auf.
	const handleGroupClick = group => {
		const values = activeGroup === group ? [] : STATUS_GROUPS[group]

		// Dasselbe Feld wie in der Filterleiste: die Kacheln sind Kurzwahl, die
		// Leiste zeigt danach denselben Stand.
		handleFilterChange(
			'invoiceStatus',
			values.map(value => ({ value, label: t(value) }))
		)
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	// Storno: Die Rechnung bleibt mit ihrer Nummer bestehen, zaehlt nicht mehr zu
	// den offenen Posten, und der Bericht wird zur Neubearbeitung freigegeben
	// (Anforderung #91). Aus dem Zeilenmenue und aus der offenen Vorschau derselbe
	// Weg — deshalb steht er hier einmal.
	const openCancel = invoice => {
		eventEmitter.emit(
			types.openInvoiceCancel,
			// Der Saldo geht mit: Aus ihm errechnet das Fenster, ob auf die
			// Rechnung schon gezahlt wurde — und sagt dann, dass das Geld als
			// Guthaben bestehen bleibt (Anforderung #91).
			{ id: invoice.id, no: invoice.no, amount: invoice.amount, balance: invoice.balance },
			fetchInvoices
		)
	}

	const downoload = id => {
		http.get(TApi.ADMIN_DOWNLOAD_INVOICES(id), {
			headers: {
				'Content-Type': 'application/pdf'
			},
			responseType: 'blob'
		}).then(({ data }) => {
			downloadFile(`Invoice_${id}`, data)
		})
	}

	// Eine Rechnung ist ein Schriftstück — der Klick auf die Zeile zeigt sie,
	// statt sie erst herunterzuladen. Mit den Pfeiltasten geht es durch die
	// Liste weiter, etwa beim Durchsehen der offenen Posten.
	const previewItems = useMemo(
		() =>
			invoices.map(invoice => ({
				name: `${t('Invoice')}${invoice.no ? ` ${invoice.no}` : ''} — ${invoice.name}.pdf`,
				url: TApi.ADMIN_DOWNLOAD_INVOICES(invoice.id),
				id: invoice.id,
				meta: [
					t(invoice.status),
					customNumberFormat(roundAmount(invoice.amount)),
					roundAmount(invoice.openCredit) > 0
						? `${t('Credit from cancellation')} € ${customNumberFormat(roundAmount(invoice.openCredit))}`
						: null,
					invoice.createdAt
				]
					.filter(Boolean)
					.join(' · ')
			})),
		[invoices, t]
	)

	// Der Dialog liegt über der Zeile, aus der er geöffnet wurde — er muss also
	// selbst sagen, auf welche Rechnung gebucht wird. Deshalb gehen Nummer,
	// Status, Unternehmen und Datum mit, nicht nur die beiden Beträge.
	const paymentDialogInvoice = invoice => ({
		id: invoice.id,
		no: invoice.no,
		amount: invoice.amount,
		balance: invoice.balance,
		status: invoice.status,
		company: invoice.name,
		school: invoice.school,
		createdAt: invoice.createdAt
	})

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchInvoices()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, debouncedSearch, filterQuery])

	const columns = useMemo(
		() => [
			{
				Header: t('Status'),
				accessor: 'status',
				Cell: ({ row }) => {
					return (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '110px',
								height: '100%'
							}}
						>
							<div className={row.original.statusStyle} style={{ fontWeight: 800 }}>
								{t(row.original.status)}
							</div>
							{/* Alumni (#213): keine Buchung, keine Stornierung. */}
							{!readOnly && (
								<BtnTooltip
									items={[
										{
											// Auf eine stornierte Rechnung wird nichts mehr
											// gebucht — sie ist kein offener Posten mehr.
											disabled:
												row.original.status === 'Paid' || row.original.status === 'Cancelled',
											label: t('Change status'),
											onClick: () => {
												eventEmitter.emit(
													types.openInvoiceChangeData,
													paymentDialogInvoice(row.original),
													fetchInvoices
												)
											}
										},
										{
											disabled: row.original.status === 'Cancelled',
											label: t('Cancel invoice'),
											onClick: () => openCancel(row.original)
										}
									]}
								/>
							)}
						</div>
					)
				},
				width: 105
			},

			{
				Header: t('Fällig (Tage)'),
				headerToolTip: t('Deadline (d)'),
				accessor: 'overdueDays',
				width: 70
			},
			{
				Header: t('Betrag'),
				accessor: 'amount',
				Cell: ({ row }) => {
					// Die Gutschrift aus Kursen ist schon abgezogen; darunter steht,
					// wie viel und wofür — sonst passt der Betrag nicht zum Bericht.
					const kurs = roundAmount(row.original.courseCreditAmount)
					return (
						<>
							{customNumberFormat(Math.round(row.values.amount * 100) / 100)}
							{kurs > 0 && (
								<span
									className={styles.credit}
									title={t('{{n}} course completions credited', {
										n: row.original.courseCreditCount || 0
									})}
								>
									−{customNumberFormat(kurs)} {t('Courses')}
								</span>
							)}
						</>
					)
				},
				width: 80
			},
			{
				Header: t('Saldo'),
				accessor: 'balance',
				Cell: ({ row }) => {
					// Eine stornierte, aber bezahlte Rechnung hat Saldo null und
					// trotzdem Geld auf sich liegen. Ohne diese Zeile waere das
					// Guthaben in der Liste unsichtbar (Anforderung #91).
					const credit = roundAmount(row.original.openCredit)
					return (
						<>
							{customNumberFormat(roundAmount(row.values.balance))}
							{credit > 0 && (
								<span className={styles.credit} title={t('Credit from cancellation')}>
									+{customNumberFormat(credit)}
								</span>
							)}
						</>
					)
				},
				width: 75
			},
			{
				Header: t('Rechnungsnummer'),
				headerToolTip: t('Invoice Number'),
				accessor: 'id',
				width: 90,
				Cell: ({ row }) => row.original.no || null
			},
			{
				// Die Liste beginnt mit dem Status, nicht mit einem Namen — der
				// Datensatz heisst nach der Firma, deshalb steht die Namensspalte
				// hier ausdruecklich (siehe UI/Table).
				primary: true,
				Header: t('Unternehmen'),
				accessor: 'name',
				Cell: ({ row }) => {
					return <span style={{ overflow: 'hidden' }}>{row.values.name}</span>
				},
				width: 105
			},
			{
				Header: t('Form'),
				headerToolTip: t('Unternehmensform'),
				accessor: 'type',
				width: 50
			},
			{
				Header: t('Bundesland'),
				accessor: 'state',
				// Kurzform (NÖ, OÖ, Stmk …), voller Name beim Darueberfahren.
				cellToolTip: ({ row }) => stateFullLabel(row.original.state),
				Cell: ({ row }) => stateLabel(row.original.state),
				width: 70
			},
			{
				Header: t('Schule'),
				accessor: 'school',
				Cell: ({ row }) => {
					return <span style={{ overflow: 'hidden' }}>{row.values.school}</span>
				},
				width: 140
			},
			{
				Header: t('Bericht'),
				accessor: 'period',
				// Umbrechen statt abschneiden: "Halbjahresbericht" endete sonst als
				// "Halbjah…" (#79).
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>
						{reportTypeLabel(row.values.period, row.original.type, t)}
					</span>
				),
				width: 155
			},

			{
				Header: t('Datum'),
				accessor: 'createdAt',
				width: 85
			},
			// Alumni (#213): Rechnungen sind Dateien — kein Herunterladen.
			...(readOnly
				? []
				: [
						{
							Header: '',
							accessor: 'btnDownload',
							disableSortBy: true,
							Cell: ({ row }) => {
								return (
									<Btn
										icon="download"
										iconSize={{ w: 24, h: 24 }}
										theme="download"
										onClick={() => {
											downoload(row.original.id)
										}}
									/>
								)
							},
							width: 50
						}
				  ])
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, readOnly]
	)

	const downoloadList = fileType => {
		setLoading(true)
		http.get(TApi.ADMIN_DOWNOLOAD_INVOICES_LIST, {
			params: {
				type: fileType,
				leng: localStorage.getItem('i18nextLng')
			},
			responseType: 'arraybuffer'
		})
			.then(res => {
				let blob = new Blob([res.data], {
					type: blobTypes[fileType]
				})
				downloadFile('Invoices', blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('invoices')}>
				<div className={styles.miniHeaderContent}>
					<Search
						onChange={e => handleSearchChange(e.target.value)}
						value={search}
						onSubmit={e => e.preventDefault()}
						placeholder={t('Search a invoice')}
					/>
					<BtnFilter icon="arrow" onClick={() => setOpenFilter(true)} count={filters.count}>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination
					limit={limit}
					page={page}
					label={t('invoices')}
					countAll={paginationCount}
					onChange={setPage}
				/>
				{!readOnly && (
					<div className={styles.downloadContent}>
						{t('Download list as')}
						<div>
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('XLS')
									}}
								>
									XLS
								</Btn>
							)}
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('PDF')
									}}
								>
									PDF
								</Btn>
							)}
							{loading && (
								<Icon
									className={clsx(styles.icon, styles.rootLoading)}
									size={{ w: 16, h: 16 }}
									name="loading"
									color={'#22404D'}
								/>
							)}
						</div>
					</div>
				)}
			</div>
			<div className={styles.reportCards}>
				<ReportsCard
					title={t('Invoices, total')}
					value={`€ ${customNumberFormat(stats.totalAmount)}`}
					percent={amountShare(stats.totalAmount)}
					sub={t('invoicesCountOf', { n: stats.totalCount, total: stats.totalCount })}
					onClick={() => handleGroupClick('total')}
					active={activeGroup === 'total'}
					actionLabel={t('invoicesCardHint')}
				/>
				<ReportsCard
					title={t('Invoices, paid')}
					value={`€ ${customNumberFormat(stats.paidAmount)}`}
					percent={amountShare(stats.paidAmount)}
					sub={t('invoicesCountOf', { n: stats.paidCount, total: stats.totalCount })}
					note={
						stats.creditCount
							? t(stats.creditCount === 1 ? 'invoicesCreditNoteOne' : 'invoicesCreditNote', {
									n: stats.creditCount,
									amount: `€ ${customNumberFormat(Math.abs(stats.creditAmount))}`
							  })
							: null
					}
					onClick={() => handleGroupClick('paid')}
					active={activeGroup === 'paid'}
					actionLabel={t('invoicesCardHint')}
				/>
				<ReportsCard
					title={t('Invoices, open')}
					value={`€ ${customNumberFormat(stats.openAmount)}`}
					percent={amountShare(stats.openAmount)}
					sub={t('invoicesCountOf', { n: stats.openCount, total: stats.totalCount })}
					note={stats.overdueCount ? t('invoicesOverdueNote', { n: stats.overdueCount }) : null}
					onClick={() => handleGroupClick('open')}
					active={activeGroup === 'open'}
					actionLabel={t('invoicesCardHint')}
				/>
				<ReportsCard
					title={t('Invoices, cancelled')}
					value={`€ ${customNumberFormat(stats.cancelledAmount)}`}
					sub={t(stats.cancelledCount === 1 ? 'invoicesCancelledCountOne' : 'invoicesCancelledCount', {
						n: stats.cancelledCount
					})}
					note={
						// Storniert **und** bezahlt heisst: Es liegt Geld auf dem
						// Beleg. Es steht an dieser Kachel statt in einer eigenen,
						// weil es keine fuenfte Menge von Rechnungen ist, sondern
						// eine Eigenschaft dieser (Anforderung #91).
						stats.openCreditCount
							? t(stats.openCreditCount === 1 ? 'invoicesOpenCreditNoteOne' : 'invoicesOpenCreditNote', {
									n: stats.openCreditCount,
									amount: `€ ${customNumberFormat(stats.openCreditAmount)}`
							  })
							: t('invoicesCancelledNote')
					}
					onClick={() => handleGroupClick('cancelled')}
					active={activeGroup === 'cancelled'}
					actionLabel={t('invoicesCardHint')}
				/>
			</div>
			<div className="container">
				<Table
					page={page}
					columns={columns}
					items={invoices}
					limit={limit}
					countAll={paginationCount}
					onChange={setPage}
					handleSort={handleSort}
					// Die Vorschau zeigt die Rechnung als Datei — für Alumni gesperrt.
					onRowClick={readOnly ? undefined : (invoice, row) => setPreviewIndex(row.index)}
					rowTitle={readOnly ? undefined : t('Preview')}
				/>
			</div>
			{previewIndex >= 0 && !readOnly && (
				<FilePreview
					items={previewItems}
					index={previewIndex}
					onIndexChange={setPreviewIndex}
					onClose={() => setPreviewIndex(-1)}
					onDownload={item => downoload(item.id)}
					// Aus dem Drei-Punkte-Menü der Liste: Der Status lässt sich
					// auch an der offenen Rechnung ändern.
					actions={[
						{
							label: t('Change status'),
							disabled:
								invoices[previewIndex]?.status === 'Paid' ||
								invoices[previewIndex]?.status === 'Cancelled',
							onClick: () => {
								const invoice = invoices[previewIndex]
								// Die Vorschau liegt über allem — der Dialog dahinter
								// wäre nicht zu sehen.
								setPreviewIndex(-1)
								eventEmitter.emit(
									types.openInvoiceChangeData,
									paymentDialogInvoice(invoice),
									fetchInvoices
								)
							}
						},
						{
							label: t('Cancel invoice'),
							disabled: invoices[previewIndex]?.status === 'Cancelled',
							onClick: () => {
								const invoice = invoices[previewIndex]
								setPreviewIndex(-1)
								openCancel(invoice)
							}
						}
					]}
				/>
			)}
			<FilterPanel
				open={openFilter}
				onClose={() => setOpenFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	role: state.user.role,
	branch: state.options.branch
})

export default connect(mapStateToProps)(Invoices)
